import React, { useEffect, useState } from 'react';
import { generatePrompt } from '../utils/promptTemplates';
import { XIcon, CheckIcon, ChevronDownIcon } from 'lucide-react';
import { getTopicPlaceholder } from '../utils/subjectHelpers';
import { getThemeSuggestions } from '../utils/themeHelpers';
import { getLearningObjectiveExamples, getLearningObjectiveGuidance, getPedagogicalApproach } from '../utils/learningObjectiveHelpers';
export function PromptForm({
  formData,
  setFormData,
  setGeneratedPrompt
}) {
  const [languageDropdownOpen, setLanguageDropdownOpen] = useState(false);
  const [themeSuggestions, setThemeSuggestions] = useState([]);
  const [objectiveExamples, setObjectiveExamples] = useState([]);
  const [objectiveGuidance, setObjectiveGuidance] = useState('');
  const [pedagogicalGuidance, setPedagogicalGuidance] = useState('');
  const handleChange = e => {
    const {
      name,
      value
    } = e.target;
    if (name === 'subject' && value === 'custom') {
      setFormData(prev => ({
        ...prev,
        subject: value
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    // Update theme suggestions when subject or grade level changes
    if (name === 'subject' || name === 'gradeLevel') {
      const newSubject = name === 'subject' ? value : formData.subject;
      const newGradeLevel = name === 'gradeLevel' ? value : formData.gradeLevel;
      if (newSubject && newGradeLevel) {
        const suggestions = getThemeSuggestions(newSubject, newGradeLevel);
        setThemeSuggestions(suggestions);
        // Also update learning objective examples
        const examples = getLearningObjectiveExamples(newSubject, newGradeLevel);
        setObjectiveExamples(examples);
        // Update guidance text
        setObjectiveGuidance(getLearningObjectiveGuidance(newGradeLevel));
        setPedagogicalGuidance(getPedagogicalApproach(newGradeLevel));
      }
    }
  };
  // Update suggestions when component mounts or when dependencies change
  useEffect(() => {
    if (formData.subject && formData.gradeLevel) {
      const suggestions = getThemeSuggestions(formData.subject, formData.gradeLevel);
      setThemeSuggestions(suggestions);
      // Also update learning objective examples
      const examples = getLearningObjectiveExamples(formData.subject, formData.gradeLevel);
      setObjectiveExamples(examples);
      // Update guidance text
      setObjectiveGuidance(getLearningObjectiveGuidance(formData.gradeLevel));
      setPedagogicalGuidance(getPedagogicalApproach(formData.gradeLevel));
    }
  }, [formData.subject, formData.gradeLevel]);
  const handleNegativePromptToggle = prompt => {
    setFormData(prev => {
      const negativePrompts = [...prev.negativePrompts];
      if (negativePrompts.includes(prompt)) {
        return {
          ...prev,
          negativePrompts: negativePrompts.filter(item => item !== prompt)
        };
      } else {
        return {
          ...prev,
          negativePrompts: [...negativePrompts, prompt]
        };
      }
    });
  };
  const handleCustomNegativePromptChange = e => {
    setFormData(prev => ({
      ...prev,
      customNegativePrompt: e.target.value
    }));
  };
  const addCustomNegativePrompt = () => {
    if (formData.customNegativePrompt.trim()) {
      setFormData(prev => ({
        ...prev,
        negativePrompts: [...prev.negativePrompts, prev.customNegativePrompt.trim()],
        customNegativePrompt: ''
      }));
    }
  };
  const removeNegativePrompt = prompt => {
    setFormData(prev => ({
      ...prev,
      negativePrompts: prev.negativePrompts.filter(item => item !== prompt)
    }));
  };
  // Handle language toggle
  const handleLanguageToggle = language => {
    setFormData(prev => {
      const languages = [...prev.outputLanguages];
      if (languages.includes(language)) {
        // Don't remove if it's the last language
        if (languages.length === 1) {
          return prev;
        }
        return {
          ...prev,
          outputLanguages: languages.filter(lang => lang !== language)
        };
      } else {
        return {
          ...prev,
          outputLanguages: [...languages, language]
        };
      }
    });
  };
  // Generate prompt in real-time as form fields change
  useEffect(() => {
    if (formData.subject || formData.gradeLevel || formData.promptType) {
      const prompt = generatePrompt(formData);
      setGeneratedPrompt(prompt);
    }
  }, [formData, setGeneratedPrompt]);
  const commonNegativePrompts = ['Political opinions', 'Religious views', 'Controversial topics', 'Outdated information', 'Oversimplified explanations', 'Cultural stereotypes', 'Biased language', 'Complex jargon'];
  // Available languages
  const availableLanguages = ['English', 'Arabic', 'Spanish', 'French', 'German', 'Chinese', 'Japanese', 'Russian', 'Portuguese', 'Hindi', 'Italian', 'Dutch', 'Korean', 'Turkish', 'Swedish'];
  // Get the appropriate topic placeholder based on the selected subject
  const topicPlaceholder = getTopicPlaceholder(formData.subject);
  return <div className="bg-white p-6 rounded-lg shadow-md border-t-4 border-indigo-600" data-id="element-68">
      <h2 className="text-xl font-semibold text-indigo-800 mb-4" data-id="element-69">
        Customise Your Prompt
      </h2>
      <div className="space-y-4" data-id="element-70">
        <div data-id="element-71">
          <label className="block text-gray-700 font-medium mb-2" data-id="element-72">
            Prompt Type
          </label>
          <select name="promptType" value={formData.promptType} onChange={handleChange} className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-73">
            <option value="" data-id="element-74">Select a prompt type</option>
            <option value="lessonPlan" data-id="element-75">Lesson Plan Creation</option>
            <option value="assessment" data-id="element-76">Assessment Creation</option>
            <option value="differentiation" data-id="element-77">Differentiation Strategies</option>
            <option value="classroomManagement" data-id="element-78">Classroom Management</option>
            <option value="contentCreation" data-id="element-79">Educational Content</option>
            <option value="simplification" data-id="element-80">Simplify Complex Topics</option>
            <option value="curriculumStandards" data-id="element-81">
              Curriculum Standards Alignment
            </option>
          </select>
        </div>
        <div data-id="element-82">
          <label className="block text-gray-700 font-medium mb-2" data-id="element-83">
            Year Group
          </label>
          <select name="gradeLevel" value={formData.gradeLevel} onChange={handleChange} className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-84">
            <option value="" data-id="element-85">Select year group</option>
            <optgroup label="Pre-School" data-id="element-86">
              <option value="nursery" data-id="element-87">Nursery (2-3 years)</option>
              <option value="preK" data-id="element-88">Pre-K (3-4 years)</option>
              <option value="kindergarten" data-id="element-89">Reception (4-5 years)</option>
            </optgroup>
            <optgroup label="Primary School" data-id="element-90">
              <option value="grade1" data-id="element-91">Year 1 - KS1 (5-6 years)</option>
              <option value="grade2" data-id="element-92">Year 2 - KS1 (6-7 years)</option>
              <option value="grade3" data-id="element-93">Year 3 - KS2 (7-8 years)</option>
              <option value="grade4" data-id="element-94">Year 4 - KS2 (8-9 years)</option>
              <option value="grade5" data-id="element-95">Year 5 - KS2 (9-10 years)</option>
              <option value="grade6" data-id="element-96">Year 6 - KS2 (10-11 years)</option>
            </optgroup>
            <optgroup label="Secondary School" data-id="element-97">
              <option value="grade7" data-id="element-98">Year 7 - KS3 (11-12 years)</option>
              <option value="grade8" data-id="element-99">Year 8 - KS3 (12-13 years)</option>
              <option value="grade9" data-id="element-100">Year 9 - KS3 (13-14 years)</option>
              <option value="grade10" data-id="element-101">Year 10 - KS4 (14-15 years)</option>
              <option value="grade11" data-id="element-102">Year 11 - KS4 (15-16 years)</option>
              <option value="grade12" data-id="element-103">Year 12/13 - KS5 (16-18 years)</option>
            </optgroup>
            <optgroup label="Higher Education" data-id="element-104">
              <option value="foundation" data-id="element-105">Foundation Year</option>
              <option value="undergraduate" data-id="element-106">Undergraduate</option>
              <option value="postgraduate" data-id="element-107">Postgraduate</option>
            </optgroup>
            <optgroup label="Other" data-id="element-108">
              <option value="vocational" data-id="element-109">Vocational Training</option>
              <option value="adultEd" data-id="element-110">Adult Education</option>
              <option value="professionalDev" data-id="element-111">Professional Development</option>
            </optgroup>
          </select>
        </div>
        <div data-id="element-112">
          <label className="block text-gray-700 font-medium mb-2" data-id="element-113">
            Subject
          </label>
          <select name="subject" value={formData.subject} onChange={handleChange} className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-114">
            <option value="" data-id="element-115">Select a subject</option>
            <option value="Mathematics" data-id="element-116">Mathematics</option>
            <option value="Science" data-id="element-117">Science (General)</option>
            <option value="Biology" data-id="element-118">Biology</option>
            <option value="Chemistry" data-id="element-119">Chemistry</option>
            <option value="Physics" data-id="element-120">Physics</option>
            <option value="English" data-id="element-121">English/Language Arts</option>
            <option value="Literature" data-id="element-122">Literature</option>
            <option value="History" data-id="element-123">History</option>
            <option value="SocialStudies" data-id="element-124">Social Studies</option>
            <option value="Geography" data-id="element-125">Geography</option>
            <option value="ComputerScience" data-id="element-126">Computing</option>
            <option value="DesignTechnology" data-id="element-127">Design and Technology</option>
            <option value="ForeignLanguages" data-id="element-128">Modern Foreign Languages</option>
            <option value="Art" data-id="element-129">Art</option>
            <option value="Music" data-id="element-130">Music</option>
            <option value="PhysicalEducation" data-id="element-131">Physical Education</option>
            <option value="Economics" data-id="element-132">Economics</option>
            <option value="Psychology" data-id="element-133">Psychology</option>
            <option value="Philosophy" data-id="element-134">Philosophy</option>
            <option value="custom" data-id="element-135">Custom Subject...</option>
          </select>
        </div>
        {formData.subject === 'custom' && <div data-id="element-136">
            <label className="block text-gray-700 font-medium mb-2" data-id="element-137">
              Custom Subject
            </label>
            <input type="text" name="customSubject" value={formData.customSubject} onChange={handleChange} placeholder="Enter your subject" className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-138" />
          </div>}
        <div data-id="element-139">
          <label className="block text-gray-700 font-medium mb-2" data-id="element-140">
            Specific Topic
          </label>
          <input type="text" name="topic" value={formData.topic} onChange={handleChange} placeholder={topicPlaceholder} className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-141" />
        </div>
        <div data-id="element-142">
          <label className="block text-gray-700 font-medium mb-2" data-id="element-143">
            Learning Objectives
          </label>
          <textarea name="learningObjectives" value={formData.learningObjectives} onChange={handleChange} placeholder="What should students learn or be able to do? | Specify the Learning Objectives" className="w-full border border-gray-300 rounded-md px-3 py-2 h-24 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-144" />
          {formData.subject && formData.gradeLevel ? <div className="mt-2 space-y-3" data-id="element-145">
              {objectiveGuidance && <div className="text-sm text-gray-600 bg-blue-50 p-3 rounded-md border border-blue-100" data-id="element-146">
                  <p className="font-medium mb-1" data-id="element-147">Guidance:</p>
                  <p data-id="element-148">{objectiveGuidance}</p>
                </div>}
              {pedagogicalGuidance && <div className="text-sm text-gray-600 bg-indigo-50 p-3 rounded-md border border-indigo-100" data-id="element-149">
                  <p data-id="element-150">{pedagogicalGuidance}</p>
                </div>}
              {objectiveExamples.length > 0 && <div className="space-y-2" data-id="element-151">
                  <p className="text-sm text-gray-600 font-medium" data-id="element-152">
                    Example objectives for this age group and subject:
                  </p>
                  <div className="space-y-1" data-id="element-153">
                    {objectiveExamples.map((example, index) => <div key={index} className="text-sm bg-green-50 p-2 rounded-md border border-green-100 cursor-pointer hover:bg-green-100" onClick={() => setFormData(prev => {
                // If there's already content, add a new line
                const prefix = prev.learningObjectives ? prev.learningObjectives + '\n' : '';
                return {
                  ...prev,
                  learningObjectives: prefix + example
                };
              })} data-id="element-154">
                        {example}
                      </div>)}
                  </div>
                </div>}
            </div> : <div className="text-sm text-gray-500 italic mt-2" data-id="element-155">
              Select a subject and year group to see example learning objectives
            </div>}
        </div>
        <div data-id="element-156">
          <label className="block text-gray-700 font-medium mb-2" data-id="element-157">
            Students' Prior Knowledge
          </label>
          <textarea name="priorKnowledge" value={formData.priorKnowledge || ''} onChange={handleChange} placeholder="What do your students already know about this topic? What foundational skills do they have?" className="w-full border border-gray-300 rounded-md px-3 py-2 h-24 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-158" />
        </div>
        <div data-id="element-159">
          <label className="block text-gray-700 font-medium mb-2" data-id="element-160">
            Theme or Context Suggestions
          </label>
          {formData.subject && formData.gradeLevel ? <div className="space-y-2" data-id="element-161">
              <textarea name="themeContext" value={formData.themeContext || ''} onChange={handleChange} placeholder="Enter a theme or context for your lesson..." className="w-full border border-gray-300 rounded-md px-3 py-2 h-24 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-162" />
              {themeSuggestions.length > 0 && <div className="mt-2 space-y-2" data-id="element-163">
                  <p className="text-sm text-gray-600 font-medium" data-id="element-164">
                    Suggestions based on age group and subject:
                  </p>
                  <div className="space-y-1" data-id="element-165">
                    {themeSuggestions.map((suggestion, index) => <div key={index} className="text-sm bg-indigo-50 p-2 rounded-md border border-indigo-100 cursor-pointer hover:bg-indigo-100" onClick={() => setFormData(prev => ({
                ...prev,
                themeContext: suggestion
              }))} data-id="element-166">
                        {suggestion}
                      </div>)}
                  </div>
                </div>}
            </div> : <div className="text-sm text-gray-500 italic p-3 bg-gray-50 rounded-md border border-gray-200" data-id="element-167">
              Select a subject and year group to see theme suggestions
            </div>}
        </div>
        <div data-id="element-168">
          <label className="block text-gray-700 font-medium mb-2" data-id="element-169">
            AI Role (What should the AI act as?)
          </label>
          <select name="aiRole" value={formData.aiRole} onChange={handleChange} className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-170">
            <option value="" data-id="element-171">Select an AI role</option>
            <option value="subjectExpert" data-id="element-172">Subject Matter Expert</option>
            <option value="teacher" data-id="element-173">Experienced Teacher</option>
            <option value="tutor" data-id="element-174">Personal Tutor</option>
            <option value="curriculumDesigner" data-id="element-175">Curriculum Designer</option>
            <option value="educationalPsychologist" data-id="element-176">
              Educational Psychologist
            </option>
            <option value="assessmentSpecialist" data-id="element-177">Assessment Specialist</option>
            <option value="contentCreator" data-id="element-178">Educational Content Creator</option>
            <option value="simplifier" data-id="element-179">Simplifier of Complex Concepts</option>
          </select>
        </div>
        <div data-id="element-180">
          <label className="block text-gray-700 font-medium mb-2" data-id="element-181">
            Output Languages
          </label>
          <div className="relative" data-id="element-182">
            <button type="button" onClick={() => setLanguageDropdownOpen(!languageDropdownOpen)} className="w-full flex justify-between items-center border border-gray-300 rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-183">
              <span data-id="element-184">
                {formData.outputLanguages.length === 1 ? formData.outputLanguages[0] : `${formData.outputLanguages.length} languages selected`}
              </span>
              <ChevronDownIcon size={18} className="text-gray-500" data-id="element-185" />
            </button>
            {languageDropdownOpen && <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto" data-id="element-186">
                {availableLanguages.map(language => <div key={language} className="flex items-center px-3 py-2 hover:bg-gray-100 cursor-pointer" onClick={() => handleLanguageToggle(language)} data-id="element-187">
                    <div className="w-6 h-6 flex items-center justify-center mr-2 border border-gray-300 rounded" data-id="element-188">
                      {formData.outputLanguages.includes(language) && <CheckIcon size={16} className="text-indigo-600" data-id="element-189" />}
                    </div>
                    <span data-id="element-190">{language}</span>
                  </div>)}
              </div>}
          </div>
          {formData.outputLanguages.length > 0 && <div className="flex flex-wrap gap-2 mt-2" data-id="element-191">
              {formData.outputLanguages.map(language => <div key={language} className="flex items-center bg-indigo-50 px-2 py-1 rounded-md border border-indigo-200 text-sm" data-id="element-192">
                  <span className="text-indigo-700" data-id="element-193">{language}</span>
                  {formData.outputLanguages.length > 1 && <button type="button" onClick={() => handleLanguageToggle(language)} className="ml-1.5 text-indigo-500 hover:text-indigo-700" data-id="element-194">
                      <XIcon size={14} data-id="element-195" />
                    </button>}
                </div>)}
            </div>}
        </div>
        <div data-id="element-196">
          <label className="block text-gray-700 font-medium mb-2" data-id="element-197">
            Negative Prompts (What to avoid)
          </label>
          <div className="flex flex-wrap gap-2 mb-3" data-id="element-198">
            {commonNegativePrompts.map(prompt => <button key={prompt} type="button" onClick={() => handleNegativePromptToggle(prompt)} className={`text-sm px-3 py-1.5 rounded-full ${formData.negativePrompts.includes(prompt) ? 'bg-indigo-100 text-indigo-800 border border-indigo-300' : 'bg-gray-100 text-gray-800 border border-gray-200 hover:bg-gray-200'}`} data-id="element-199">
                {prompt}
              </button>)}
          </div>
          <div className="flex items-center gap-2 mb-3" data-id="element-200">
            <input type="text" value={formData.customNegativePrompt} onChange={handleCustomNegativePromptChange} placeholder="Add custom negative prompt" className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" onKeyDown={e => e.key === 'Enter' && addCustomNegativePrompt()} data-id="element-201" />
            <button type="button" onClick={addCustomNegativePrompt} disabled={!formData.customNegativePrompt.trim()} className="px-4 py-2 bg-indigo-600 text-white rounded-md disabled:bg-indigo-300" data-id="element-202">
              Add
            </button>
          </div>
          {formData.negativePrompts.length > 0 && <div className="bg-gray-50 p-3 rounded-md border border-gray-200" data-id="element-203">
              <p className="text-sm font-medium text-gray-700 mb-2" data-id="element-204">
                Selected to avoid:
              </p>
              <div className="flex flex-wrap gap-2" data-id="element-205">
                {formData.negativePrompts.map(prompt => <div key={prompt} className="flex items-center bg-white px-2 py-1 rounded-md border border-gray-200 text-sm" data-id="element-206">
                    <span data-id="element-207">{prompt}</span>
                    <button type="button" onClick={() => removeNegativePrompt(prompt)} className="ml-1.5 text-gray-500 hover:text-gray-700" data-id="element-208">
                      <XIcon size={14} data-id="element-209" />
                    </button>
                  </div>)}
              </div>
            </div>}
        </div>
        <div data-id="element-210">
          <label className="block text-gray-700 font-medium mb-2" data-id="element-211">
            Additional Context
          </label>
          <textarea name="additionalContext" value={formData.additionalContext} onChange={handleChange} placeholder="Any specific requirements, student needs, or classroom context?" className="w-full border border-gray-300 rounded-md px-3 py-2 h-24 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-212" />
        </div>
        <div data-id="element-213">
          <label className="block text-gray-700 font-medium mb-2" data-id="element-214">
            Time Constraint
          </label>
          <select name="timeConstraint" value={formData.timeConstraint} onChange={handleChange} className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-215">
            <option value="" data-id="element-216">Select time constraint</option>
            <option value="15min" data-id="element-217">15 minutes</option>
            <option value="30min" data-id="element-218">30 minutes</option>
            <option value="45min" data-id="element-219">45 minutes</option>
            <option value="60min" data-id="element-220">60 minutes (1 hour)</option>
            <option value="90min" data-id="element-221">90 minutes (1.5 hours)</option>
            <option value="120min" data-id="element-222">120 minutes (2 hours)</option>
            <option value="oneDay" data-id="element-223">One day</option>
            <option value="multiDay" data-id="element-224">Multi-day</option>
            <option value="oneWeek" data-id="element-225">One week</option>
            <option value="oneUnit" data-id="element-226">Complete unit</option>
            <option value="oneSemester" data-id="element-227">Full term</option>
          </select>
        </div>
        <div data-id="element-228">
          <label className="block text-gray-700 font-medium mb-2" data-id="element-229">
            Preferred Output Format
          </label>
          <select name="outputFormat" value={formData.outputFormat} onChange={handleChange} className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-230">
            <option value="" data-id="element-231">Select output format</option>
            <option value="detailed" data-id="element-232">Detailed</option>
            <option value="bullet" data-id="element-233">Bullet Points</option>
            <option value="stepByStep" data-id="element-234">Step-by-Step</option>
            <option value="table" data-id="element-235">Table Format</option>
            <option value="script" data-id="element-236">Script/Dialogue</option>
            <option value="presentation" data-id="element-237">Presentation Slides</option>
            <option value="standardTemplate" data-id="element-238">Standard Template</option>
          </select>
        </div>
      </div>
    </div>;
}